// Edge-safe legacy-URL mapping (pure string/URL logic only — NO Node imports,
// so it can be imported by middleware.ts which runs on the Edge runtime).
//
// The site was migrated from an old real-estate CRM whose indexed URLs use a
// different shape (/imovel/{id}/{slug}, /imovel/?finalidade=..., /mobile/imovel/...).
// Those URLs now 404/5xx. This helper translates the *search/listing* and *mobile*
// shapes onto the current routes so they 30x to a valid (approximate) page instead.
//
// Property-detail URLs (/imovel/{id}/...) are intentionally NOT handled here: they
// are resolved to their exact canonical URL by the catch-all pages
// (pages/imovel/[id]/[...slug].tsx and pages/mobile/imovel/[id]/...), which reuse
// getShortImovelServerSideProps and emit a single 308 to /imoveis/.../imovel/{id}.

// Old CRM "finalidade" terms -> the only three values the /imoveis route accepts.
// IMPORTANT: the search page strictly checks for 'venda'|'aluguel'|'temporada'
// (anything else, including 'locacao', silently falls back to 'venda'), so the
// mapping MUST be applied here.
import { resolverBrokerGestor, siteBrokerGestor } from './legacy-broker-gestor';

const FINALIDADE_TO_MODEL: Record<string, string> = {
  venda: 'venda',
  vendas: 'venda',
  vender: 'venda',
  compra: 'venda',
  comprar: 'venda',
  locacao: 'aluguel',
  locacoes: 'aluguel',
  aluguel: 'aluguel',
  alugar: 'aluguel',
  aluga: 'aluguel',
  rent: 'aluguel',
  'para-alugar': 'aluguel',
  'a-venda': 'venda',
  'para-venda': 'venda',
  // Site da Mold Systems: imóvel nas duas finalidades sai como "venda-e-locacao".
  'venda-e-locacao': 'venda',
  temporada: 'temporada',
};

const DEFAULT_MODEL = 'venda';

// Mirrors utils/FunctionsGlobals.removerAcentos so the slug we emit matches what the
// search page's normalizeSegmentForLookup produces when matching city/type/district.
const COMBINING_DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');
const SPACING_DIACRITICS = new RegExp(
  '[\\u00B4\\u0060\\u005E\\u007E\\u00A8\\u02C6\\u02CB\\u02CA]',
  'g'
);
const foldAccents = (value: string): string =>
  String(value || '')
    .normalize('NFKD')
    .replace(COMBINING_DIACRITICS, '')
    .replace(SPACING_DIACRITICS, '')
    .toLowerCase();

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// Slugify a single already-decoded term into ONE safe path segment.
// Crucially, '/', '\\', '?' and '#' are neutralized so a value can never inject
// extra path segments / query structure into the destination URL.
const slugify = (decoded: string): string =>
  foldAccents(decoded.trim())
    .replace(/[/\\?#]+/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '') || '-';

// A single-value term (e.g. city) -> path segment resolved by NAME on the search
// page (spaces -> '-', accents folded, lowercased). Empty -> '-'.
const toLookupSegment = (value: string | null | undefined): string =>
  slugify(safeDecode(String(value || '')));

// A possibly multi-value term (type/district) -> the search page splits these
// segments on '+', so comma-separated CRM values must be re-joined with '+'.
const toMultiSegment = (value: string | null | undefined): string => {
  const parts = safeDecode(String(value || ''))
    .split(',')
    .map((part) => slugify(part))
    .filter((part) => part !== '-');
  return parts.length ? parts.join('+') : '-';
};

// District values arrive as "<name>-<numericId>" (e.g. "jardim-lageado-290568"),
// "0"/empty when absent, and may be comma-separated. Strip the trailing numeric
// id from each value and re-join with '+'.
const toDistrictSegment = (value: string | null | undefined): string => {
  const raw = safeDecode(String(value || '')).trim();
  if (!raw || raw === '0') return '-';
  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && part !== '0')
    .map((part) => slugify(part.replace(/-\d+$/, '')))
    .filter((part) => part !== '-');
  return parts.length ? parts.join('+') : '-';
};

const isAllDigits = (value: string | undefined | null): boolean =>
  !!value && /^\d+$/.test(value);

// INC-0280: segmento que é lixo de template/JS, nunca slug nem id de verdade — placeholder de rota
// `[id]` (cru ou %5Bid%5D, vindo do _buildManifest/__NEXT_DATA__) ou `undefined`/`null`/`NaN` saídos de
// `${valor}` no front. Onde ele ocupa o lugar do id a resposta é 404, não redirect (ver site-props).
const PLACEHOLDER_DE_ROTA = /^\[.+\]$/;
const VALOR_JS_CRU = /^(undefined|null|nan)$/i;

export const ehPlaceholderDeRota = (segment: string | null | undefined): boolean =>
  PLACEHOLDER_DE_ROTA.test(safeDecode(String(segment ?? '')).trim());

export const ehSegmentoCru = (segment: string | null | undefined): boolean => {
  const valor = safeDecode(String(segment ?? '')).trim();
  return PLACEHOLDER_DE_ROTA.test(valor) || VALOR_JS_CRU.test(valor);
};

const positiveIntOrNull = (value: string | null | undefined): string | null => {
  const raw = String(value ?? '').trim();
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return String(parsed);
};

const mapModel = (term: string | null | undefined): string | null => {
  const folded = foldAccents(String(term || '').trim());
  if (!folded) return null;
  return FINALIDADE_TO_MODEL[folded] || null;
};

// Finalidade canônica ('venda' | 'aluguel' | 'temporada') de um segmento de /imoveis/{model},
// ou null quando o termo não é uma finalidade conhecida. Usado também pelo SSR (imóvel removido).
export const modeloCanonicoDaBusca = (segment: string | null | undefined): string | null =>
  mapModel(safeDecode(String(segment || '')));

// Translate a legacy /imovel search/listing URL (no numeric id) to the new
// /imoveis/{model}/{city}/{district}/{company}/{type} route + best-effort filters.
const buildSearchRedirect = (afterImovel: string[], params: URLSearchParams): string => {
  let model: string | null = null;
  let typeFromPath: string | undefined;
  let cityFromPath: string | undefined;
  let districtFromPath: string | undefined;

  // Friendly path form: /imovel/{finalidade}/{tipo}/{cidade}/{bairro-id}
  if (afterImovel.length > 0) {
    const pathModel = mapModel(afterImovel[0]);
    if (pathModel) {
      model = pathModel;
      typeFromPath = afterImovel[1];
      cityFromPath = afterImovel[2];
      districtFromPath = afterImovel[3];
    }
  }

  if (!model) {
    model = mapModel(params.get('finalidade')) || DEFAULT_MODEL;
  }

  // type: path positional > tipo > val (when val is a type label, not a price).
  // May be multi-value (comma-separated) -> joined with '+' for the search route.
  const valParam = params.get('val');
  const valIsType = !!valParam && !/^\s*\d/.test(valParam);
  const type = toMultiSegment(typeFromPath || params.get('tipo') || (valIsType ? valParam : ''));

  const city = toLookupSegment(cityFromPath || params.get('cid') || params.get('cidade') || '');

  const district = toDistrictSegment(districtFromPath || params.get('bairro') || '');

  const out = new URLSearchParams();
  const min = positiveIntOrNull(params.get('vmi'));
  const max = positiveIntOrNull(params.get('vma'));
  const dormitorios = positiveIntOrNull(params.get('dor'));
  const suites = positiveIntOrNull(params.get('sui'));
  const banheiros = positiveIntOrNull(params.get('ban'));
  const vagas = positiveIntOrNull(params.get('gar'));
  const pagination = positiveIntOrNull(params.get('pag'));

  if (min) out.set('min', min);
  if (max) out.set('max', max);
  if (dormitorios) out.set('dormitorios', dormitorios);
  if (suites) out.set('suites', suites);
  if (banheiros) out.set('banheiros', banheiros);
  if (vagas) out.set('vagas', vagas);
  if (pagination) out.set('pagination', pagination);

  // Preserve attribution params if present.
  for (const utm of ['utm_source', 'utm_medium', 'utm_id', 'utm_campaign']) {
    const value = params.get(utm);
    if (value) out.set(utm, value);
  }

  const basePath = `/imoveis/${model}/${city}/${district}/-/${type}`;
  const query = out.toString();
  return query ? `${basePath}?${query}` : basePath;
};

// Ficha de imóvel do site da Mold Systems (CRM de origem de clientes migrados, ex.: cs2603):
// /imovel/{finalidade}/{tipo}/{cidade}/{bairro}/{REFERENCIA}, ex.:
// /imovel/venda/casas/aracatuba/jardim-nova-yorque/1269. A referência é a mesma que a
// importação grava em tbl_imovel.Referencia. Sem esta checagem a URL caía em
// buildSearchRedirect() e a ficha virava uma LISTAGEM.
// Exatamente 5 segmentos: o formato de busca do CRM antigo (/imovel/{finalidade}/{tipo}/
// {cidade}/{bairro-id}) tem 4 e continua indo para a busca.
const REFERENCIA_MOLD = /^(?=[A-Za-z0-9]*\d)[A-Za-z0-9]{1,20}$/;

/** Referência da ficha no formato Mold Systems, ou null quando o path não é esse formato. */
export const referenciaDaFichaMold = (afterImovel: string[]): string | null => {
  if (afterImovel.length !== 5 || !mapModel(afterImovel[0])) return null;
  const referencia = safeDecode(String(afterImovel[4] || '')).trim();
  return REFERENCIA_MOLD.test(referencia) ? referencia : null;
};

/** Busca equivalente a uma ficha Mold cuja referência não está (mais) publicada. */
export const buscaDaFichaMold = (afterImovel: string[], params: URLSearchParams): string =>
  buildSearchRedirect(afterImovel.slice(0, 4), params);

// Ficha do site antigo no formato /imovel/{slug-descritivo}/{REFERENCIA} — Tecimob e afins
// (cs2586 danicorrea: /imovel/apartamento-a-venda-no-bairro-petropolis-em-porto-alegre-rs/240;
// cs2606 marcelloeoliveira: /imovel/{slug}/CA36575 — o fix 9d9e0486 de julho nunca chegou à prod).
// O slug é decorativo; quem identifica é a referência (tbl_imovel.Referencia). Exatamente 2 segmentos,
// o 1º NÃO numérico (numérico = /imovel/{id}/{slug}, nosso formato curto) e NÃO finalidade (busca do CRM
// antigo, /imovel/{finalidade}/{tipo}); o 2º no mesmo formato de referência do Mold. Sem isto a URL caía
// em buildSearchRedirect() e a ficha virava a busca genérica /imoveis/venda/-/-/-/-.
const referenciaDaFichaSlugRef = (afterImovel: string[]): string | null => {
  if (afterImovel.length !== 2) return null;
  const slug = safeDecode(String(afterImovel[0] || '')).trim();
  if (!slug || isAllDigits(slug) || mapModel(slug) || ehSegmentoCru(slug)) return null;
  const referencia = safeDecode(String(afterImovel[1] || '')).trim();
  return REFERENCIA_MOLD.test(referencia) ? referencia : null;
};

// INC-0287 (cs2599 Welcam): ficha do site antigo num segmento só, /imovel/{slug-descritivo}-code-{REFERENCIA}
// (ex.: /imovel/apartamento-jardim-gralha-azul-code-1660; o "code" é a Referência do CRM). Quem identifica é
// a referência — NUNCA o id: o parseImovelId do SSR arrancaria os dígitos do segmento e geraria id falso.
const FICHA_CODE = /^.+-code-([A-Za-z0-9]{1,20})$/i;

const referenciaDaFichaCode = (afterImovel: string[]): string | null => {
  if (afterImovel.length !== 1) return null;
  const segmento = safeDecode(String(afterImovel[0] || '')).trim();
  const referencia = FICHA_CODE.exec(segmento)?.[1] || '';
  return REFERENCIA_MOLD.test(referencia) ? referencia : null;
};

/**
 * Referência da ficha de site antigo (Mold de 5 segmentos, /imovel/{slug}/{REF} ou /imovel/{slug}-code-{REF}),
 * ou null.
 */
export const referenciaDaFichaLegada = (afterImovel: string[]): string | null =>
  referenciaDaFichaMold(afterImovel) || referenciaDaFichaSlugRef(afterImovel) || referenciaDaFichaCode(afterImovel);

// Slug do formato {slug}/{REF}: "{tipo}-a-venda-no-bairro-{bairro}-em-{cidade}-{uf}",
// "{tipo}-para-alugar-em-{cidade}-{uf}"… A cidade é o trecho depois do ÚLTIMO "-em-" (o tipo pode
// ter "em": "casa-em-condominio-…"); o bairro, o que fica entre "-no-bairro-" e esse "-em-".
const FINALIDADE_NO_SLUG: Array<[RegExp, string]> = [
  [/(^|-)a-venda(-|$)/, 'venda'],
  [/(^|-)para-(alugar|locacao)(-|$)/, 'aluguel'],
  [/(^|-)para-temporada(-|$)/, 'temporada'],
];

/**
 * Busca equivalente a uma ficha de site antigo cuja referência não está (mais) publicada: Mold → a busca
 * dos 4 primeiros segmentos; {slug}/{REF} → finalidade/cidade/bairro tirados do slug. '' quando o slug não
 * diz nada (quem chama cai na busca padrão do site).
 */
export const buscaDaFichaLegada = (
  afterImovel: string[],
  params: URLSearchParams,
  modeloPadrao: string = DEFAULT_MODEL
): string => {
  if (referenciaDaFichaMold(afterImovel)) return buscaDaFichaMold(afterImovel, params);
  if (!referenciaDaFichaSlugRef(afterImovel)) return '';
  const slug = foldAccents(safeDecode(String(afterImovel[0] || '')).trim());
  const finalidade = FINALIDADE_NO_SLUG.find(([re]) => re.test(slug))?.[1] || null;
  const local = /^(.*)-em-(.+?)-[a-z]{2}$/.exec(slug);
  const cidade = local ? slugify(local[2]) : '-';
  const bairroMatch = local ? /-no-bairro-(.+)$/.exec(local[1]) : null;
  const bairro = bairroMatch ? slugify(bairroMatch[1]) : '-';
  if (cidade === '-' && !finalidade) return '';
  return `/imoveis/${finalidade || modeloPadrao || DEFAULT_MODEL}/${cidade}/${cidade === '-' ? '-' : bairro}/-/-`;
};

/**
 * Returns the destination (path, optionally with ?query) a legacy URL should
 * redirect to, or `null` when the request must be passed through unchanged
 * (valid routes + property-detail URLs handled by the catch-all pages).
 */
export function buildLegacyRedirect(
  rawPathname: string,
  params: URLSearchParams
): string | null {
  // Next default trailingSlash:false — normalize so '/imovel/' === '/imovel'.
  let pathname = String(rawPathname || '/');
  if (pathname.length > 1) pathname = pathname.replace(/\/+$/, '');

  const lower = pathname.toLowerCase();

  // Strip the legacy /mobile prefix — the new site is responsive (no /mobile routes).
  const isMobile = lower === '/mobile' || lower.startsWith('/mobile/');
  const rest = isMobile ? pathname.slice('/mobile'.length) || '/' : pathname;
  const restLower = rest.toLowerCase();

  const isImovel = restLower === '/imovel' || restLower.startsWith('/imovel/');

  // INC-0287 (cs2599 Welcam): a busca do site antigo era /buscar?cidade=…&tipo=… — rota que não existe
  // aqui (404). Vai para a busca nova com os mesmos filtros que a busca antiga do CRM já traduz (cidade,
  // tipo, finalidade, faixas de preço, UTMs); sem filtro, a busca geral /imoveis/venda/-/-/-/-.
  if (restLower === '/buscar' || restLower.startsWith('/buscar/')) {
    return buildSearchRedirect(rest.split('/').filter(Boolean).slice(1), params);
  }

  // /imoveis/{sinônimo}/... (alugar, locacao, para-alugar, a-venda…) -> a finalidade canônica.
  // A busca só entende venda|aluguel|temporada: qualquer outra grafia virava VENDA em silêncio
  // (INC-0079 cs2451: /imoveis/alugar/... listava imóveis à venda e estava indexada no Google).
  if (!isMobile && lower.startsWith('/imoveis/')) {
    const segments = pathname.split('/'); // ['', 'imoveis', model, ...]
    const model = modeloCanonicoDaBusca(segments[2]);
    if (!model || model === segments[2]) return null;
    segments[2] = model;
    const query = params.toString();
    return query ? `${segments.join('/')}?${query}` : segments.join('/');
  }

  if (isImovel) {
    const afterImovel = rest.split('/').filter(Boolean).slice(1); // drop 'imovel'

    // INC-0280: /imovel/{undefined|null|NaN|[id]} (e placeholder `[x]` em qualquer segmento) passa
    // direto: o SSR (getShortImovelServerSideProps) responde 404. Antes virava 308 para a busca genérica.
    if ((afterImovel.length === 1 && ehSegmentoCru(afterImovel[0])) || afterImovel.some(ehPlaceholderDeRota)) {
      return null;
    }

    // Property detail (/imovel/{numericId}/...) -> let the catch-all pages resolve
    // it to the canonical URL (single 308). Pass through here.
    if (isAllDigits(afterImovel[0])) {
      return null;
    }

    // Ficha de site antigo por REFERÊNCIA (Mold de 5 segmentos, /imovel/{slug}/{REF} ou
    // /imovel/{slug}-code-{REF}) -> quem resolve é pages/imovel/[id] (busca a referência e redirect
    // permanente para a canônica; sem imóvel, a busca do slug). Vinda do /mobile, passa antes pela versão desktop.
    if (referenciaDaFichaLegada(afterImovel)) {
      if (!isMobile) return null;
      const query = params.toString();
      return query ? `${rest}?${query}` : rest;
    }

    // Legacy search/listing URL -> translate to the new search route.
    return buildSearchRedirect(afterImovel, params);
  }

  // Any other /mobile/* path -> desktop equivalent, preserving the query string.
  if (isMobile) {
    if (rest === '/') return '/';
    const query = params.toString();
    return query ? `${rest}?${query}` : rest;
  }

  return null;
}

// INC-0287 (cs2599 Welcam): URLs indexadas com o PRÓPRIO domínio como 1º segmento, às vezes repetido
// (/www.welcam.com.br/www.welcam.com.br/imovel/…) — link relativo mal montado no site antigo. Compara sem
// "www." dos dois lados e sem porta; só o host da requisição conta (nunca outro domínio qualquer).
const hostSemWww = (valor: string): string =>
  safeDecode(String(valor || ''))
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, '')
    .replace(/\.+$/, '')
    .replace(/^www\./, '');

/** Path sem os prefixos iguais ao host da requisição, ou null quando o path não começa pelo host. */
export const removerPrefixoDoHost = (rawPathname: string, requestHost: string): string | null => {
  const host = hostSemWww(requestHost);
  if (!host || !host.includes('.')) return null;
  const segmentos = String(rawPathname || '/').split('/').filter(Boolean);
  let inicio = 0;
  while (inicio < segmentos.length && hostSemWww(segmentos[inicio]) === host) inicio += 1;
  if (inicio === 0) return null;
  return `/${segmentos.slice(inicio).join('/')}`;
};

// INC-0278 (cs2320 Terras Prósperas): /imoveis/venda/são-josé/-/-/chácara respondia 200 e se declarava canônica,
// e o Google indexava a versão COM acento ao lado da sem acento (a dos sitemaps). A busca casa cidade, bairro,
// empreendimento e tipo por removerAcentos + minúsculas (no cliente e no SSR): as duas são a MESMA listagem.
// Segmento de filtro com acento (NFC ou NFD) ou maiúscula → o mesmo segmento normalizado (foldAccents = a regra
// de utils/FunctionsGlobals.removerAcentos + toLowerCase). Fica como está: a finalidade (tratada acima), o
// código digitado (7º segmento), a ficha (…/imovel/{id}) e segmento que não dá para remontar com segurança.
const SEGMENTO_NAO_REMONTAVEL = /[/?#\\%]/;
// encodeURIComponent escapa sub-delims que são válidos no path; '+' é o separador da seleção múltipla.
const encodarSegmento = (valor: string): string =>
  encodeURIComponent(valor).replace(/%(24|26|2B|2C|3A|3B|3D|40)/gi, (esc) => decodeURIComponent(esc));

const normalizarSegmentoListagem = (segment: string): string => {
  if (!segment || segment === '-' || ehSegmentoCru(segment)) return segment;
  const decoded = safeDecode(segment);
  // '%' depois de decodificar = escape malformado ou %25: não dá para remontar sem mudar o significado
  if (SEGMENTO_NAO_REMONTAVEL.test(decoded)) return segment;
  const normalizado = foldAccents(decoded);
  if (!normalizado || normalizado === decoded || foldAccents(normalizado) !== normalizado) return segment;
  return encodarSegmento(normalizado);
};

/**
 * Path da LISTAGEM (/imoveis/{finalidade}/{cidade}/{bairro}/{empreendimento}/{tipo}[/{código}]) com os 4
 * segmentos de filtro normalizados, ou null quando não há nada a mudar (já normalizado, não é listagem,
 * finalidade desconhecida, ficha ou placeholder cru — esse continua 404 no SSR, #1149).
 */
export const normalizarCaminhoListagem = (pathname: string): string | null => {
  const segments = String(pathname || '').split('/'); // ['', 'imoveis', model, city, district, company, type, code?]
  if (segments[1] !== 'imoveis' || segments.length > 8) return null;
  if (!modeloCanonicoDaBusca(segments[2]) || segments.some(ehPlaceholderDeRota)) return null;
  let mudou = false;
  for (let i = 3; i <= 6 && i < segments.length; i += 1) {
    const novo = normalizarSegmentoListagem(segments[i]);
    if (novo !== segments[i]) {
      segments[i] = novo;
      mudou = true;
    }
  }
  return mudou ? segments.join('/') : null;
};

// INC-0278: na navegação do CLIENTE (`_next/data`) o Next põe os parâmetros da rota na query
// (?model=venda&city=são-josé&district=-…) e o 301 os levaria para a barra do navegador. Tira só os que
// ESPELHAM o segmento do caminho pedido — qualquer outro parâmetro (UTM, filtros, codsite) fica.
const PARAMETROS_DA_ROTA_LISTAGEM = ['model', 'city', 'district', 'company', 'type', 'code'];
export const semParametrosDaRota = (params: URLSearchParams, pathname: string): URLSearchParams => {
  const segments = String(pathname || '').split('/'); // ['', 'imoveis', model, city, district, company, type, code?]
  const limpos = new URLSearchParams(params);
  if (segments[1] !== 'imoveis') return limpos;
  PARAMETROS_DA_ROTA_LISTAGEM.forEach((nome, i) => {
    const segment = segments[i + 2];
    const valores = limpos.getAll(nome);
    if (segment !== undefined && valores.length === 1 && valores[0] === safeDecode(segment)) limpos.delete(nome);
  });
  return limpos;
};

// INC-0278: status 301 (decisão do Daniel) só no redirect que normaliza a listagem; os legados seguem 308.
export type AcaoUrlLegada = { tipo: 'redirect' | 'rewrite'; destino: string; status?: 301 };

// Destino de redirect que é listagem já sai normalizado — um salto só (sinônimo da finalidade, prefixo do
// domínio e acento na mesma resposta, sem cadeia).
const comListagemNormalizada = (acao: AcaoUrlLegada): AcaoUrlLegada => {
  if (acao.tipo !== 'redirect') return acao;
  const queryIndex = acao.destino.indexOf('?');
  const path = queryIndex === -1 ? acao.destino : acao.destino.slice(0, queryIndex);
  const normalizado = normalizarCaminhoListagem(path);
  if (normalizado === null) return acao;
  return { tipo: 'redirect', destino: normalizado + (queryIndex === -1 ? '' : acao.destino.slice(queryIndex)), status: 301 };
};

/**
 * O que o middleware faz com a URL: redirect permanente (destino com ?query), rewrite interno (o path sem o
 * prefixo do host, quando quem responde é a página da ficha — ela mesma dá o único redirect para a canônica,
 * sem cadeia) ou null (passa direto, como sempre).
 */
export function resolverUrlLegada(
  rawPathname: string,
  params: URLSearchParams,
  requestHost: string,
  hostDeTeste: boolean = false
): AcaoUrlLegada | null {
  const semPrefixo = removerPrefixoDoHost(rawPathname, requestHost);
  // INC-0332 (cs2588 Mégga): URLs do site antigo (Broker Gestor) — só nos tenants cadastrados em
  // utils/legacy-broker-gestor.ts (host em produção; ?codsite= nos hosts de teste). Nos demais sites, nada muda.
  const brokerGestor = siteBrokerGestor(requestHost, params, hostDeTeste);
  if (brokerGestor) {
    const acaoBrokerGestor = resolverBrokerGestor(semPrefixo ?? rawPathname, params, brokerGestor);
    if (acaoBrokerGestor) return acaoBrokerGestor;
  }
  const destino = buildLegacyRedirect(semPrefixo ?? rawPathname, params);
  if (destino) return comListagemNormalizada({ tipo: 'redirect', destino });

  const query = params.toString();
  if (semPrefixo === null) {
    // INC-0278: a própria URL pedida é uma listagem com acento/maiúscula → 301 para a normalizada
    const normalizado = normalizarCaminhoListagem(rawPathname);
    if (normalizado === null) return null;
    return { tipo: 'redirect', destino: query ? `${normalizado}?${query}` : normalizado, status: 301 };
  }

  const comQuery = query ? `${semPrefixo}?${query}` : semPrefixo;
  // /imovel/… e /mobile/imovel/… que o buildLegacyRedirect deixa passar são resolvidos pela página (id,
  // referência de site antigo ou 404): servir pelo rewrite evita prefixo → ficha antiga → canônica.
  if (/^\/(mobile\/)?imovel\//i.test(semPrefixo)) return { tipo: 'rewrite', destino: comQuery };
  return comListagemNormalizada({ tipo: 'redirect', destino: comQuery });
}
